import _ from 'underscore';
import Backbone from 'backbone';
import { ToolbarView as BaseToolbarView } from 'backbone-toolbar';

const isEdge = window && window.navigator.userAgent.indexOf('Edge') > -1;

export class ToolbarView extends BaseToolbarView {
  overrideWith({ view }) {
    this.el.parentNode.replaceChild(view.el, this.el);
    return () => {
      view.el.parentNode.replaceChild(this.el, view.el);
    };
  }

  /**
   * Make the toolbar visible
   * @returns {ToolbarView} - The toolbar itself
   * @fires ToolbarView#event:visibilityChanged
   */
  show() {
    if (isEdge) {
      // Work around for Edge bug that can crash browser when working with narrator:
      // hide with styles instead of calling this.$el.show(). same in hide()
      this.$el.css({
        height: 'auto',
        overflow: 'visible',
      });
    } else {
      this.$el.show();
    }
    this.$el.attr('aria-hidden', 'false');

    this.hidden = false;

    /**
     * The visibility of the toolbar is changed. Pass the new visibility.
     * @event ToolbarView#event:visibilityChanged
     * @type {boolean}
     */
    this.trigger('visibilityChanged', true);
    return this;
  }

  /**
   * Make the toolbar hidden
   * @returns {ToolbarView} - The toolbar itself
   * @fires ToolbarView#event:visibilityChanged
   */
  hide() {
    if (isEdge) {
      // Work around for Edge bug that can crash browser when working with narrator:
      // hide with styles instead of calling this.$el.hide(). same in show()
      this.$el.css({
        height: 0,
        overflow: 'hidden',
      });
    } else {
      this.$el.hide();
    }
    this.$el.attr('aria-hidden', 'true');

    this.hidden = true;

    /**
     * The visibility of the toolbar is changed. Pass the new visibility.
     * @event ToolbarView#event:visibilityChanged
     * @type {boolean}
     */
    this.trigger('visibilityChanged', false);
    return this;
  }

  /**
   * Check if the toolbar is hidden
   * @returns {boolean} - True if the toolbar is hidden
   */
  isHidden() {
    return this.hidden;
  }
}

export class OverridableToolbarView extends Backbone.View {
  initialize(options) {
    const toolbarViewOptions = _.pick(options, 'toolbarId', 'classes', 'items');

    this.toolbarView = new ToolbarView(toolbarViewOptions);
    this.overriddenView = null;
  }

  render() {
    this.toolbarView.render();
    this.$el.html((this.overriddenView || this.toolbarView).el);
    return this;
  }

  overrideWith(view) {
    if (!this.overriddenView && view) {
      this.overriddenView = view;
      this.el.replaceChild(view.el, this.toolbarView.el);
    } else if (this.overriddenView) {
      throw new Error('Cannot override again before restoring toolbar.');
    } else {
      throw new Error('Need to provide backbone view to override toolbar.');
    }
  }

  restoreToolbar() {
    if (this.overriddenView) {
      this.el.replaceChild(this.toolbarView.el, this.overriddenView.el);
      this.overriddenView = null;
    }
  }

  isOverridden() {
    return Boolean(this.overriddenView);
  }

  remove() {
    this.toolbarView.remove();
    super.remove();
  }
}

_.each([
  'get',
  'set',
  'update',
  'show',
  'hide',
  'isHidden',
], (name) => {
  if (_.isFunction(ToolbarView.prototype[name])) {
    OverridableToolbarView.prototype[name] = function func(...args) {
      return ToolbarView.prototype[name].apply(this.toolbarView, args);
    };
  }
});
